import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Snackbar, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import BackendAxios from "../BackendAxios";
import { StyledButton } from "../HomePage/index";
import GalleryAssetGrid from "./components/GalleryAssetGrid";
import GalleryViewerDialog from "./components/GalleryViewerDialog";

function Gallery(props) {
  const { sizing } = props;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerState, setViewerState] = useState({
    assetIndex: null,
  });
  const [planMatchCache, setPlanMatchCache] = useState({});
  const [selectedPlanEntry, setSelectedPlanEntry] = useState(null);
  const [planViewOpen, setPlanViewOpen] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
  });

  const isPhone = (sizing?.vw || window.innerWidth) < 600;
  const contentHeight = isPhone
    ? `${(sizing?.vh || window.innerHeight) - (sizing?.xs?.usedHeight || 0)}px`
    : `${(sizing?.vh || window.innerHeight) - (sizing?.sm?.usedHeight || 0)}px`;

  useEffect(() => {
    let isMounted = true;

    async function fetchGallery() {
      setLoading(true);
      setError(null);

      try {
        const response = await BackendAxios.get("/gallery");
        if (!isMounted) {
          return;
        }

        setCategories(response.data?.categories || []);
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        setError(fetchError.response?.data?.error || "Failed to load gallery");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchGallery();

    return () => {
      isMounted = false;
    };
  }, []);

  const orderedAssets = useMemo(
    () =>
      categories.flatMap((category) =>
        (category?.assets || []).map((asset, assetIndex) => ({
          ...asset,
          _categoryId: category?._id || category?.slug || category?.name || "",
          _categoryName: category?.name || "",
          _orderIndex: assetIndex,
        }))
      ),
    [categories]
  );

  const safeActiveIndex =
    orderedAssets.length > 0 && viewerState.assetIndex !== null
      ? ((viewerState.assetIndex % orderedAssets.length) + orderedAssets.length) %
        orderedAssets.length
      : 0;
  const activeAsset =
    viewerState.assetIndex !== null ? orderedAssets[safeActiveIndex] || null : null;
  const activeCategoryName = String(activeAsset?._categoryName || "").trim();
  const activePlanMatch = activeCategoryName ? planMatchCache[activeCategoryName] || null : null;
  const canViewActivePlan = activePlanMatch?.status === "matched" && Boolean(activePlanMatch.entry);

  useEffect(() => {
    let isMounted = true;

    async function fetchPlanMatch(categoryName) {
      setPlanMatchCache((prevState) => {
        if (prevState[categoryName]?.status === "loading") {
          return prevState;
        }

        return {
          ...prevState,
          [categoryName]: {
            status: "loading",
            entry: null,
          },
        };
      });

      try {
        const response = await BackendAxios.get("/entries/browse/match", {
          params: { name: categoryName },
        });

        if (!isMounted) {
          return;
        }

        setPlanMatchCache((prevState) => ({
          ...prevState,
          [categoryName]: {
            status: response.data?.entry ? "matched" : "missing",
            entry: response.data?.entry || null,
          },
        }));
      } catch (matchError) {
        if (!isMounted) {
          return;
        }

        setPlanMatchCache((prevState) => ({
          ...prevState,
          [categoryName]: {
            status: "missing",
            entry: null,
          },
        }));
      }
    }

    if (
      viewerState.assetIndex !== null &&
      activeCategoryName &&
      !planMatchCache[activeCategoryName]
    ) {
      fetchPlanMatch(activeCategoryName);
    }

    return () => {
      isMounted = false;
    };
  }, [activeCategoryName, planMatchCache, viewerState.assetIndex]);

  const openViewer = (assetIndex) => {
    setViewerState({ assetIndex });
  };

  const closeViewer = () => {
    setViewerState({ assetIndex: null });
    setPlanViewOpen(false);
    setSelectedPlanEntry(null);
  };

  const openMatchedPlan = () => {
    if (canViewActivePlan) {
      setSelectedPlanEntry(activePlanMatch.entry);
      setPlanViewOpen(true);
    }
  };

  const navigateViewer = (direction) => {
    if (!orderedAssets.length) {
      return;
    }

    setViewerState((prevState) => ({
      ...prevState,
      assetIndex:
        direction === "next"
          ? ((prevState.assetIndex ?? 0) + 1) % orderedAssets.length
          : ((prevState.assetIndex ?? 0) - 1 + orderedAssets.length) % orderedAssets.length,
    }));
  };

  useEffect(() => {
    if (viewerState.assetIndex === null) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        navigateViewer("next");
      } else if (event.key === "ArrowLeft") {
        navigateViewer("prev");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [viewerState.assetIndex, orderedAssets.length]);

  return (
    <Grid2
      container
      spacing={0}
      xs={12}
      sx={{
        width: "100%",
        height: contentHeight,
        minHeight: 0,
        overflow: "hidden",
        background: "linear-gradient(180deg, #f3efe7 0%, #f7f3ec 100%)",
      }}
    >
      <Grid2 xs={12}>
        <Box
          sx={{
            height: contentHeight,
            minHeight: 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              action={
                <StyledButton
                  variant="contained"
                  onClick={() => window.location.reload()}
                  sx={{
                    backgroundColor: "#5B7195",
                    "&:hover": { backgroundColor: "#4a5d7a" },
                  }}
                >
                  Retry
                </StyledButton>
              }
            >
              {error}
            </Alert>
          ) : orderedAssets.length === 0 ? (
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.78)",
                borderRadius: "28px",
                p: { xs: 3, sm: 5 },
                textAlign: "center",
                boxShadow: "0 18px 34px rgba(14,18,23,0.08)",
                margin: "auto 16px",
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>
                Gallery coming soon
              </Typography>
              <Typography variant="body1" color="text.secondary">
                No gallery images are live yet.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", width: "100%" }}>
              <GalleryAssetGrid
                assets={orderedAssets}
                onOpenAsset={openViewer}
                availableHeight={
                  (sizing?.vh || window.innerHeight) -
                  (isPhone ? sizing?.xs?.usedHeight || 0 : sizing?.sm?.usedHeight || 0)
                }
                keyboardEnabled={viewerState.assetIndex === null}
              />
            </Box>
          )}
        </Box>
      </Grid2>

      <GalleryViewerDialog
        open={viewerState.assetIndex !== null}
        assets={orderedAssets}
        activeIndex={safeActiveIndex}
        onClose={closeViewer}
        onPrev={() => navigateViewer("prev")}
        onNext={() => navigateViewer("next")}
        onSelect={(assetIndex) =>
          setViewerState((prevState) => ({ ...prevState, assetIndex }))
        }
        isPhone={isPhone}
        sizing={sizing}
        onViewPlan={openMatchedPlan}
        canViewPlan={canViewActivePlan}
        planEntry={selectedPlanEntry}
        planViewOpen={planViewOpen}
        onClosePlanView={() => setPlanViewOpen(false)}
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={3500}
        onClose={() => setSnackbarState({ open: false, message: "" })}
        message={snackbarState.message}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      />
    </Grid2>
  );
}

export default Gallery;
